import { Component, OnInit, OnDestroy, HostListener, inject, computed, signal, effect, Injector, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { Firestore, collection, collectionData, doc, getDoc, query, where } from '@angular/fire/firestore';
import { DepartmentSchemesService, DepartmentEntry, SchemeCard, SchemeProject } from '../../services/department-schemes.service';
import { FirestoreDepartmentService } from '../../services/firestore-department.service';
import { DepartmentMatchingService } from '../../services/department-matching.service';
import { FirestoreProjectService } from '../../services/firestore-project.service';
import { AuthService } from '../../services/auth.service';
import { ApprovalService } from '../../services/approval.service';
import { UserService } from '../../services/user.service';
import { ApprovalRequest } from '../../models/approval.model';
import { UserRole } from '../../models/user.model';
import { Project } from '../../models/project.model';
import { PhotoUploadComponent } from '../../components/photo-upload/photo-upload.component';
import { CompareChangesComponent } from '../../components/shared/compare-changes/compare-changes.component';
import { PhotoUploadService } from '../../services/photo-upload.service';
import { resolveHmDepartmentName } from '../../data/hm-department-master';
import * as XLSX from 'xlsx-js-style';

@Component({
    selector: 'app-departments',
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, PhotoUploadComponent, CompareChangesComponent],
    templateUrl: './departments.component.html',
    styleUrls: ['./departments.component.css']
})
export class DepartmentsComponent implements OnInit, OnDestroy {
    departments: DepartmentEntry[] = [];
    selectedDept: DepartmentEntry | null = null;
    searchQuery: string = '';

    // Mobile panel state – show content panel on mobile
    mobileShowContent: boolean = false;

    // Drawer state
    drawerOpen: boolean = false;
    drawerScheme: SchemeCard | null = null;
    drawerDept: DepartmentEntry | null = null;

    // Add/Edit project form state
    showAddForm: boolean = false;
    formMode: 'add' | 'edit' = 'add';
    editingSlNo: number | null = null;
    editingProjectId: string | null = null;
    editingProjectPhotos = signal<string[]>([]);
    addProjectForm!: FormGroup;
    drawerHasLength: boolean = false;
    // Delete confirmation
    deletingSlNo: number | null = null;

    // Checkbox flag state (for template styling without OnPush)
    hmPriorityFlag = false;
    recurringFlag  = false;
    delayedFlag    = false;

    // Categorised staged photos (Before / During / After) for create mode
    stagedPhotosBefore = signal<Array<{ file: File; previewUrl: string }>>([]);
    stagedPhotosDuring = signal<Array<{ file: File; previewUrl: string }>>([]);
    stagedPhotosAfter  = signal<Array<{ file: File; previewUrl: string }>>([]);

    // Update toast notification
    updateToast = signal<{ title: string; lines: string[] } | null>(null);
    private toastTimer: ReturnType<typeof setTimeout> | null = null;

    // Loading / save state
    isSaving = signal(false);

    // Staged photos for new project creation (before project ID exists)
    stagedPhotoEntries = signal<Array<{ file: File; previewUrl: string }>>([]);

    // Firestore sync state
    usingFirestore = false;
    firestoreIds = new Map<string, string>(); // dept name → Firestore document ID

    // ── Bulk select / delete (drawer) ────────────────────────────────────
    selectedSlNos       = signal<Set<number>>(new Set());
    showBulkDeleteConfirm = signal(false);
    isBulkDeleting      = signal(false);
    isAllSelected = computed<boolean>(() => {
        const list = this.drawerScheme?.projectList ?? [];
        return list.length > 0 && list.every(p => this.selectedSlNos().has(p.slNo));
    });

    // ── Scheme CRUD ───────────────────────────────────────────────────────
    showSchemeForm      = signal(false);
    schemeFormMode      = signal<'add' | 'edit'>('add');
    editingSchemeIndex  = signal(-1);
    showDeleteSchemeConfirm = signal(false);
    deletingSchemeIndex = signal(-1);
    schemeForm!: FormGroup;

    private destroy$ = new Subject<void>();
    // Latest Firestore projects snapshot — used to re-merge after dept structure reloads
    private latestFsProjects: Project[] = [];
    private mappedDepartmentIds = signal<string[]>([]);
    private firestoreDeptService = inject(FirestoreDepartmentService);
    private deptMatching = inject(DepartmentMatchingService);
    private firestoreProjectService = inject(FirestoreProjectService);
    private authService = inject(AuthService);
    private approvalService = inject(ApprovalService);
    private userService = inject(UserService);
    private photoService = inject(PhotoUploadService);
    private firestore = inject(Firestore);
    private injector = inject(Injector);
    private cdr      = inject(ChangeDetectorRef);
    private deptAssignmentsSub: Subscription | null = null;

    isAdmin = computed(() => {
        const role = this.authService.currentUser()?.role;
        return role === UserRole.MINISTER || role === UserRole.ADMIN;
    });

    /** Strict admin (the Admin role only — excludes Minister/OSD). */
    isStrictAdmin   = computed(() => this.authService.currentUser()?.role === UserRole.ADMIN);
    isMinister      = computed(() => this.authService.currentUser()?.role === UserRole.MINISTER);
    isOsd           = computed(() => this.authService.currentUser()?.role === UserRole.OSD);
    isMinisterOrOsd = computed(() => this.isMinister() || this.isOsd());

    isOperator = computed(() => this.authService.currentUser()?.role === UserRole.OPERATOR);
    isApprover = computed(() => this.authService.currentUser()?.role === UserRole.APPROVER);

    /** Minister/OSD can toggle between table (default) and card view. */
    deptViewMode = signal<'table' | 'card'>('table');

    /**
     * Roles that see the compact project table view (instead of the legacy
     * scheme-cards view). Operators and approvers manage projects here;
     * ministers and OSDs view the same table read-only after navigating from
     * the dashboard's "Department Wise Status" table.
     */
    showsCompactProjectView = computed(() =>
        this.isOperator() || this.isApprover() || this.isMinisterOrOsd()
    );

    /** Column count for the compact project table's empty-state row. */
    opProjectsColspan = computed(() => {
        // OSD/Minister: #, Name, Scheme, GP, Length, Cost, Status, Updated = 8
        if (this.isMinisterOrOsd()) return 8;
        // Operator/Approver: #, Name, Scheme, District, Block, Length, Cost, Status, Progress, Updated = 10 base
        let cols = 10;
        if (this.isApprover()) cols += 1;   // Approval Status
        if (this.isOperator() || this.isApprover()) cols += 1;  // Actions
        return cols;
    });

    private static deptNameToId(name: string): string {
        return name.toLowerCase().replace(/[\s&]/g, '_').replace(/[^a-z0-9_]/g, '').replace(/_+/g, '_');
    }

    private async resolveDepartmentIdsToNames(rawIds: string[]): Promise<string[]> {
        const all = this.deptService.getDepartments();
        const byName = new Map(all.map(d => [DepartmentsComponent.deptNameToId(d.name), d.name]));
        const byShort = new Map(all.map(d => [DepartmentsComponent.deptNameToId(d.shortName), d.name]));
        const resolved: string[] = [];
        const unknown: string[] = [];

        for (const raw of rawIds) {
            const key = DepartmentsComponent.deptNameToId(raw);
            const known = byName.get(key) || byShort.get(key);
            if (known) resolved.push(known);
            else unknown.push(raw);
        }

        if (unknown.length > 0) {
            const fetchedNames = await Promise.all(unknown.map(async (deptDocId) => {
                try {
                    const snap = await getDoc(doc(this.firestore, 'departments', deptDocId));
                    return snap.exists() ? String(snap.data()?.['name'] || '').trim() : '';
                } catch {
                    return '';
                }
            }));
            resolved.push(...fetchedNames.filter(Boolean));
        }

        return Array.from(new Set(resolved));
    }

    constructor(
        private deptService: DepartmentSchemesService,
        private route: ActivatedRoute,
        private router: Router,
        private fb: FormBuilder
    ) {}

    ngOnInit(): void {
        this.subscribeDepartmentAssignments();
        // Load all departments then filter for operator role
        this.departments = this.applyDeptFilter(this.deptService.getDepartments());

        // Check for query parameter to select a specific department
        this.route.queryParams.subscribe(params => {
            const deptName = params['dept'];
            if (deptName) {
                const matching = this.deptService.getDepartmentByName(deptName);
                if (matching) {
                    this.selectDepartment(matching);
                }
            } else if (this.departments.length > 0 && !this.selectedDept) {
                this.selectedDept = this.departments[0];
            }
        });

        if (this.departments.length > 0 && !this.selectedDept) {
            this.selectedDept = this.departments[0];
        }

        if (typeof window !== 'undefined' &&
            window.matchMedia('(max-width: 767px)').matches &&
            this.showsCompactProjectView()) {
            this.mobileShowContent = true;
        }

        // Upgrade to Firestore in the background (doesn't block rendering)
        this.loadFromFirestoreIfAvailable();

        // ── Live Firestore schemes (approver-approved additions) ──────
        // Stream the `schemes` collection. Each emission injects any
        // unknown scheme into its matching dept's scheme list so that
        // the subsequent project merge can find a target card to place
        // the project under. Without this step, projects whose `scheme`
        // refers to a freshly-approved scheme would silently be dropped
        // from the table.
        (collectionData(collection(this.firestore, 'schemes'), { idField: 'id' }) as Observable<any[]>)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: schemes => {
                    this.injectApprovedSchemes(schemes);
                    if (this.latestFsProjects.length > 0) {
                        this.mergeFirestoreProjects(this.latestFsProjects);
                    }
                },
                error: err => console.warn('Dept page: schemes stream error', err)
            });

        // Live Firestore projects → overlay into scheme cards
        this.firestoreProjectService.getProjects$()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: projects => {
                    this.latestFsProjects = projects;
                    this.mergeFirestoreProjects(projects);
                },
                error: err => console.warn('Dept page: Firestore projects stream error', err)
            });

        // ── Approver-only: stream of pending requests + user directory ──
        if (this.isApprover()) {
            this.approvalService.getPendingRequests()
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: reqs => this.approverPendingRequests.set(reqs),
                    error: err => console.warn('Dept page: approver pending stream error', err)
                });

            // Cache uid → displayName for the modal meta strip
            this.userService.getAllUsers()
                .then(users => {
                    const map = new Map<string, string>();
                    users.forEach(u => map.set(u.uid, u.displayName || u.email || u.uid));
                    this.approverUserNames.set(map);
                })
                .catch(err => console.warn('Dept page: user directory load failed', err));
        }
    }

    ngOnDestroy(): void {
        this.deptAssignmentsSub?.unsubscribe();
        this.destroy$.next();
        this.destroy$.complete();
    }

    private applyDeptFilter(allDepts: DepartmentEntry[]): DepartmentEntry[] {
        const currentUser = this.authService.currentUser();
        if (currentUser?.role === UserRole.OPERATOR) {
            const allowed = new Set([
                ...(currentUser.departmentIds ?? []),
                ...(currentUser.assignedDepartments ?? []),
                ...(this.mappedDepartmentIds() ?? [])
            ].map(v => DepartmentsComponent.deptNameToId(v)));

            if (allowed.size > 0) {
                return allDepts.filter(d =>
                    allowed.has(DepartmentsComponent.deptNameToId(d.name)) ||
                    allowed.has(DepartmentsComponent.deptNameToId(d.shortName))
                );
            }
        }
        return allDepts;
    }

    private subscribeDepartmentAssignments(): void {
        effect(() => {
            const user = this.authService.currentUser();
            this.deptAssignmentsSub?.unsubscribe();
            this.mappedDepartmentIds.set([]);

            if (!user || user.role !== UserRole.OPERATOR) {
                this.departments = this.applyDeptFilter(this.deptService.getDepartments());
                if (this.selectedDept && !this.departments.some(d => d.name === this.selectedDept!.name)) {
                    this.selectedDept = this.departments[0] ?? null;
                }
                return;
            }

            const firebaseUid = this.authService.getFirebaseAuthUid();
            const candidateUserIds = Array.from(new Set([user.uid, firebaseUid].filter(Boolean) as string[]));
            const streams = candidateUserIds.map(candidateId => {
                const byIdQuery = query(
                    collection(this.firestore, 'departmentAssignments'),
                    where('userId', '==', candidateId),
                    where('isActive', '==', true)
                );
                return collectionData(byIdQuery, { idField: 'id' }) as Observable<any[]>;
            });

            this.deptAssignmentsSub = combineLatest(streams)
                .subscribe({
                    next: async rowsPerSource => {
                        const ids = rowsPerSource
                            .flat()
                            .map(r => String(r?.departmentId || '').trim())
                            .filter(Boolean);
                        const normalizedNames = await this.resolveDepartmentIdsToNames(Array.from(new Set(ids)));
                        this.mappedDepartmentIds.set(normalizedNames);
                        this.departments = this.applyDeptFilter(this.deptService.getDepartments());
                        if (this.selectedDept && !this.departments.some(d => d.name === this.selectedDept!.name)) {
                            this.selectedDept = this.departments[0] ?? null;
                        }
                    },
                    error: () => this.mappedDepartmentIds.set([])
                });
        }, { injector: this.injector });
    }

    private loadFromFirestoreIfAvailable(): void {
        this.firestoreDeptService.loadDepartmentsOnce()
            .then(fsDepts => {
                if (fsDepts.length === 0) return;
                fsDepts.forEach(d => {
                    const canonical = resolveHmDepartmentName(d.name);
                    this.firestoreIds.set(canonical, d.id);
                });
                this.deptService.mergeFromFirestore(
                    fsDepts.map(({ id: _id, ...rest }) => rest)
                );
                this.departments = this.applyDeptFilter(this.deptService.getDepartments());
                this.usingFirestore = true;
                // Re-select current dept from fresh data so the view refreshes
                if (this.selectedDept) {
                    this.selectedDept = this.departments.find(d => d.name === this.selectedDept!.name) ?? this.departments[0];
                }
                // Re-apply project overlay onto the freshly loaded dept structure.
                // The getProjects$ stream won't re-fire on its own because the
                // Firestore projects collection hasn't changed — only our local dept
                // reference was replaced, so we must re-merge manually.
                if (this.latestFsProjects.length > 0) {
                    this.mergeFirestoreProjects(this.latestFsProjects);
                }
            })
            .catch(e => console.warn('Departments Firestore load failed — using static data.', e));
    }

    /** Compute scheme-level aggregates from a project list.
     *  Values remain in Lakhs to match the unit displayed throughout the app. */
    private calculateSchemeStats(projectList: SchemeProject[]): {
        totalProjects: number; totalCost: number; spent: number;
        completed: number; inProgress: number; stuck: number; planned: number;
    } {
        return {
            totalProjects: projectList.length,
            totalCost:  projectList.reduce((s, p) => s + (p.costLakh  ?? 0), 0),
            spent:      projectList.reduce((s, p) => s + (p.spentLakh ?? 0), 0),
            completed:  projectList.filter(p => p.status === 'Completed').length,
            inProgress: projectList.filter(p => p.status === 'In Progress').length,
            stuck:      projectList.filter(p => p.status === 'Stuck').length,
            planned:    projectList.filter(p => p.status === 'Planned').length,
        };
    }

    /** Fuzzy-match a raw scheme name against a dept's SchemeCard list.
     *  Handles abbreviations like "BGBO(25-26)" → "Bikashita Gaon Bikashita Odisha (BGBO)" */
    private findSchemeCard(schemes: { name: string }[], rawName: string): { name: string } | undefined {
        if (!rawName) return undefined;
        const norm = (s: string) => s.toLowerCase().replace(/[\s\-_\/\.]+/g, ' ').trim();
        const n = norm(rawName);
        for (const s of schemes) {
            const sn = norm(s.name);
            if (sn === n || sn.includes(n)) return s;
            const ac = s.name.match(/\(([A-Z][A-Z0-9]+)\)/);
            if (ac) {
                const a = ac[1].toLowerCase();
                if (n === a || n.startsWith(a + ' ') || n.startsWith(a + '-') || n.startsWith(a + '(')) return s;
            }
            const fw = sn.split(' ')[0];
            if (fw.length >= 4 && n.startsWith(fw)) return s;
        }
        return undefined;
    }

    /**
     * Add any Firestore-only `schemes` collection entries to the matching
     * dept's `schemes` array so subsequent `mergeFirestoreProjects()` can
     * locate them. Dedupes by lowercase name and matches dept by full name
     * OR shortName (case-insensitive) so the operator dropdown's free-text
     * dept value still resolves to the right master entry.
     */
    private injectApprovedSchemes(rows: Array<{ id?: string; name?: string; dept?: string }>): void {
        if (!Array.isArray(rows) || rows.length === 0) return;

        for (const row of rows) {
            if (!row?.name || !row?.dept) continue;
            const deptKey = String(row.dept).toLowerCase();
            const dept = this.departments.find(d =>
                d.name.toLowerCase() === deptKey ||
                d.shortName.toLowerCase() === deptKey
            );
            if (!dept) continue;

            const exists = (dept.schemes ?? []).some(s => s.name.toLowerCase() === row.name!.toLowerCase());
            if (exists) continue;

            dept.schemes.push({
                name: row.name,
                projects: 0,
                totalCost: 0,
                spent: 0,
                completed: 0, inProgress: 0, stuck: 0, planned: 0,
            } as SchemeCard);
        }

        // Refresh `selectedDept` reference so any computed signals re-fire
        if (this.selectedDept) {
            this.selectedDept = this.departments.find(d => d.name === this.selectedDept!.name) ?? this.selectedDept;
        }
        if (this.drawerScheme && this.drawerDept) {
            const freshDept = this.departments.find(d => d.name === this.drawerDept!.name);
            this.drawerScheme = freshDept?.schemes.find(s => s.name === this.drawerScheme!.name) ?? this.drawerScheme;
        }
    }

    /** Overlay Firestore projects into matching scheme cards and recalculate all stats. */
    private mergeFirestoreProjects(fsProjects: Project[]): void {
        // Ministers see only escalated projects (HM Priority | Recurring Intervention | Delayed).
        // latestFsProjects always holds the full unfiltered set; the gate is applied here at
        // merge time so re-merges (triggered by scheme updates) stay consistent with role.
        const isMinister = this.authService.currentUser()?.role === UserRole.MINISTER;
        const projectsToMerge = isMinister
            ? fsProjects.filter(p =>
                !!p.hmPriority ||
                !!p.recurringIntervention ||
                !!(p as any).isDelayed
              )
            : fsProjects;

        // Step 1 – Clear all scheme lists; live `projects` collection is the only source
        this.departments.forEach(dept =>
            dept.schemes.forEach(scheme => {
                scheme.projectList = [];
                scheme.projects   = 0;
                scheme.totalCost  = 0;
                scheme.spent      = 0;
                scheme.completed  = 0;
                scheme.inProgress = 0;
                scheme.stuck      = 0;
                scheme.planned    = 0;
            })
        );

        // Step 2 – Inject fresh Firestore projects into matching scheme cards
        projectsToMerge.forEach(proj => {
            if (!proj.scheme?.trim() || !proj.dept?.trim()) return;

            const dept = this.deptMatching.resolveDepartment(proj, this.departments);
            if (!dept) return;

            // Resolve scheme; auto-create one inline if missing so newly-approved
            // schemes aren't dropped on the merge before the schemes-stream
            // listener has a chance to inject them.
            let scheme = this.findSchemeCard(dept.schemes, proj.scheme) as (typeof dept.schemes)[0] | undefined;
            if (!scheme) {
                scheme = {
                    name: proj.scheme,
                    projects: 0,
                    totalCost: 0,
                    spent: 0,
                    completed: 0, inProgress: 0, stuck: 0, planned: 0,
                    projectList: [],
                } as SchemeCard;
                dept.schemes.push(scheme);
            }

            if (!scheme.projectList) scheme.projectList = [];
            scheme.projectList.push({
                id:                   proj.id,
                slNo:                 0,   // renumbered in step 3
                district:             'Bhadrak',
                division:             (proj as any).block ?? proj.division ?? proj.loc ?? '',
                constituency:         proj.loc ?? 'Dhamnagar',
                gp:                   (proj as any).gp ?? (proj as any).gpName ?? '',
                roadName:             proj.name,
                lengthKm:             proj.roadLength,
                costLakh:             proj.cost  ?? 0,
                spentLakh:            proj.spent ?? 0,
                physical:             (proj as any).physical ?? 0,
                status:               proj.status as SchemeProject['status'],
                hmPriority:           proj.hmPriority,
                recurringIntervention: proj.recurringIntervention,
                photos:               proj.photos,
                // Extra fields for OSD/Minister table & export
                executantName:        (proj as any).executorName ?? (proj as any).executant?.name ?? '',
                executantContact:     (proj as any).executorNumber ?? (proj as any).executant?.contact ?? '',
                yearOfCreation:       (proj as any).yearOfCreation ?? (proj as any).sanctionedYear ?? '',
                projectId:            (proj as any).projectId || proj.id || '',
                updatedAt:            (proj as any).updatedAt,
                statusCategory:       (proj as any).statusCategory ?? '',
            } as any);
        });

        // Step 3 – Renumber slNos and recalculate ALL stats purely from projectList
        this.departments.forEach(dept =>
            dept.schemes.forEach(scheme => {
                const list = scheme.projectList ?? [];
                list.forEach((p, i) => p.slNo = i + 1);
                const stats = this.calculateSchemeStats(list);
                scheme.projects   = stats.totalProjects;
                scheme.totalCost  = stats.totalCost;
                scheme.spent      = stats.spent;
                scheme.completed  = stats.completed;
                scheme.inProgress = stats.inProgress;
                scheme.stuck      = stats.stuck;
                scheme.planned    = stats.planned;
            })
        );

        // Refresh component references and force a CD cycle so the view
        // updates even when the Firestore callback runs outside NgZone
        if (this.selectedDept) {
            this.selectedDept = this.departments.find(d => d.name === this.selectedDept!.name) ?? this.selectedDept;
        }
        if (this.drawerScheme && this.drawerDept) {
            const freshDept = this.departments.find(d => d.name === this.drawerDept!.name);
            this.drawerScheme = freshDept?.schemes.find(s => s.name === this.drawerScheme!.name) ?? this.drawerScheme;
        }
        this.cdr.detectChanges();
    }


    selectDepartment(dept: DepartmentEntry): void {
        this.selectedDept = dept;
        this.searchQuery = '';
        this.closeDrawer();
        this.mobileShowContent = true; // switch to content panel on mobile
    }

    mobileGoBack(): void {
        this.mobileShowContent = false;
        this.selectedDept = null;
    }

    /** Mobile back in scheme panel — list drill-down or leave page for compact roles. */
    handleMobileBack(): void {
        if (this.showsCompactProjectView()) {
            this.goBackToDashboard();
            return;
        }
        if (this.mobileShowContent) {
            this.mobileGoBack();
            return;
        }
        this.goBackToDashboard();
    }

    goToHome(): void {
        this.router.navigate(['/home']);
    }

    /**
     * Back from the department page. Minister/OSD jump back to the
     * HM Decision Dashboard ("Dashboard Overview"). Other roles go to
     * their respective landing pages.
     */
    goBackToDashboard(): void {
        const role = this.authService.currentUser()?.role;
        if (role === UserRole.MINISTER || role === UserRole.OSD) {
            this.router.navigate(['/dhamnagar-dashboard/dashboard']);
        } else if (role === UserRole.ADMIN) {
            this.router.navigate(['/dhamnagar-dashboard/user-management']);
        } else if (role === UserRole.APPROVER) {
            this.router.navigate(['/dhamnagar-dashboard/approver']);
        } else if (role === UserRole.OPERATOR) {
            this.router.navigate(['/dhamnagar-dashboard/operator']);
        } else {
            this.router.navigate(['/home']);
        }
    }

    get filteredSchemes(): SchemeCard[] {
        if (!this.selectedDept) return [];
        const q = this.searchQuery.toLowerCase();
        if (!q) return this.selectedDept.schemes;
        return this.selectedDept.schemes.filter(s => s.name.toLowerCase().includes(q));
    }

    get totalSchemes(): number { return this.selectedDept?.schemes.length ?? 0; }
    get totalProjects(): number { return this.selectedDept?.schemes.reduce((s, sc) => s + sc.projects, 0) ?? 0; }
    get totalCost(): number    { return this.selectedDept?.schemes.reduce((s, sc) => s + sc.totalCost, 0) ?? 0; }
    get totalSpent(): number   { return this.selectedDept?.schemes.reduce((s, sc) => s + sc.spent, 0) ?? 0; }
    get spentPercentage(): number {
        const cost = this.totalCost;
        return cost > 0 ? (this.totalSpent / cost) * 100 : 0;
    }

    getDeptProjectCount(dept: DepartmentEntry): number {
        return dept.schemes.reduce((s, sc) => s + sc.projects, 0);
    }

    // ── OPERATOR PROJECT TABLE ─────────────────────────────────────────
    /** Toolbar filters for the operator project table. */
    opTableSearch       = '';
    opTableStatus       = '';
    opTableScheme       = '';
    opTableProgressBand = '';                                   // '', '<30', '30-70', '>=70'
    opTableSort         = 'updated-desc';                       // 'updated-desc' | 'updated-asc' | 'cost-desc' | 'cost-asc' | 'name'

    // OSD/Minister-only extra filters (ignored for other roles)
    opTableBlock        = '';
    opTableGp           = '';
    opTableExecutant    = '';

    /** Flat list of every project in the selected department, with scheme name attached. */
    get opAllProjects(): Array<SchemeProject & { schemeName: string; schemeShort: string }> {
        if (!this.selectedDept) return [];
        const out: Array<SchemeProject & { schemeName: string; schemeShort: string }> = [];
        for (const sc of this.selectedDept.schemes) {
            const shortName = this.schemeShortForm(sc.name);
            for (const p of (sc.projectList ?? [])) {
                out.push({ ...p, schemeName: sc.name, schemeShort: shortName });
            }
        }
        return out;
    }

    /** Unique block values in the current department (OSD/Minister block filter). */
    get opAvailableBlocks(): string[] {
        const blocks = new Set<string>();
        for (const p of this.opAllProjects) {
            const b = (p as any).division;
            if (b) blocks.add(b);
        }
        return Array.from(blocks).sort();
    }

    /** Unique GP values, optionally constrained to selected block. */
    get opAvailableGps(): string[] {
        const gps = new Set<string>();
        for (const p of this.opAllProjects) {
            const anyP = p as any;
            if (this.opTableBlock && (anyP.division ?? '') !== this.opTableBlock) continue;
            const g = anyP.gp;
            if (g) gps.add(g);
        }
        return Array.from(gps).sort();
    }

    /** Unique executant names in the current department. */
    get opAvailableExecutants(): string[] {
        const execs = new Set<string>();
        for (const p of this.opAllProjects) {
            const e = (p as any).executantName;
            if (e) execs.add(e);
        }
        return Array.from(execs).sort();
    }

    /** Unique scheme short forms in the current department (for the filter dropdown). */
    get opAvailableSchemes(): Array<{ short: string; full: string }> {
        if (!this.selectedDept) return [];
        return this.selectedDept.schemes.map(s => ({
            short: this.schemeShortForm(s.name),
            full:  s.name,
        }));
    }

    // ─── OSD-only: multi-select scheme filter (checkbox dropdown) ───
    /** Schemes selected via checkboxes (OSD only). Empty set = no scheme constraint. */
    opSelectedSchemes = signal<Set<string>>(new Set<string>());
    /** Whether the multi-select dropdown panel is open. */
    isOpSchemesMenuOpen = signal(false);

    toggleOpSchemesMenu(): void { this.isOpSchemesMenuOpen.update(v => !v); }
    closeOpSchemesMenu(): void  { this.isOpSchemesMenuOpen.set(false); }

    /** Toggle one scheme in the multi-select set. */
    toggleSchemeInSelection(short: string): void {
        this.opSelectedSchemes.update(prev => {
            const next = new Set(prev);
            next.has(short) ? next.delete(short) : next.add(short);
            return next;
        });
    }

    isSchemeSelected(short: string): boolean {
        return this.opSelectedSchemes().has(short);
    }

    clearSchemeSelection(): void {
        this.opSelectedSchemes.set(new Set<string>());
    }

    selectAllSchemes(): void {
        this.opSelectedSchemes.set(new Set(this.opAvailableSchemes.map(s => s.short)));
    }

    /** Compact label for the dropdown button — e.g. "PMGSY, MMSY +1" or "All Schemes". */
    get opSelectedSchemesLabel(): string {
        const sel = Array.from(this.opSelectedSchemes());
        if (sel.length === 0) return 'All Schemes';
        if (sel.length === 1) return sel[0];
        if (sel.length === 2) return sel.join(', ');
        return `${sel.slice(0, 2).join(', ')} +${sel.length - 2}`;
    }

    /** Filtered + sorted view of opAllProjects driven by the toolbar.
     *  Smart search matches: project name, scheme (full + short), district,
     *  block / division / constituency, slNo (project sequence), and any
     *  Firestore-assigned `projectId` / doc id.
     */
    get opFilteredProjects(): Array<SchemeProject & { schemeName: string; schemeShort: string }> {
        const q = this.opTableSearch.trim().toLowerCase();
        let rows = this.opAllProjects.filter(p => {
            if (q) {
                const anyP = p as any;
                const hay = [
                    p.roadName,
                    p.schemeShort,
                    p.schemeName,
                    p.district,
                    p.constituency,
                    anyP.division,
                    anyP.block,
                    anyP.gp,
                    p.slNo,
                    anyP.projectId,
                    anyP.id,
                ].filter(v => v !== undefined && v !== null)
                 .map(v => String(v).toLowerCase())
                 .join(' ');
                if (!hay.includes(q)) return false;
            }
            if (this.opTableStatus && (p.status ?? 'Planned') !== this.opTableStatus) return false;

            // Scheme filter:
            //   - OSD/Minister use the multi-select checkbox dropdown (opSelectedSchemes).
            //   - All other roles use the single-select <select> (opTableScheme).
            if (this.isMinisterOrOsd()) {
                const sel = this.opSelectedSchemes();
                if (sel.size > 0 && !sel.has(p.schemeShort)) return false;
            } else if (this.opTableScheme && p.schemeShort !== this.opTableScheme) {
                return false;
            }
            // OSD/Minister extra filters
            if (this.isMinisterOrOsd()) {
                const anyP = p as any;
                if (this.opTableBlock     && (anyP.division ?? '')       !== this.opTableBlock)     return false;
                if (this.opTableGp        && (anyP.gp ?? '')             !== this.opTableGp)        return false;
                if (this.opTableExecutant && (anyP.executantName ?? '')  !== this.opTableExecutant) return false;
            }
            // Progress band: operator/approver only
            if (!this.isMinisterOrOsd() && this.opTableProgressBand) {
                const physical = (p as any).physical ?? 0;
                if (this.opTableProgressBand === '<30'   && physical >= 30) return false;
                if (this.opTableProgressBand === '30-70' && (physical < 30 || physical >= 70)) return false;
                if (this.opTableProgressBand === '>=70'  && physical < 70) return false;
            }
            return true;
        });

        switch (this.opTableSort) {
            case 'updated-desc': rows = rows.sort((a: any, b: any) => this.tsMs(b.updatedAt) - this.tsMs(a.updatedAt)); break;
            case 'updated-asc':  rows = rows.sort((a: any, b: any) => this.tsMs(a.updatedAt) - this.tsMs(b.updatedAt)); break;
            case 'cost-desc':    rows = rows.sort((a, b) => (b.costLakh ?? 0) - (a.costLakh ?? 0)); break;
            case 'cost-asc':     rows = rows.sort((a, b) => (a.costLakh ?? 0) - (b.costLakh ?? 0)); break;
            case 'name':         rows = rows.sort((a, b) => (a.roadName ?? '').localeCompare(b.roadName ?? '')); break;
        }
        return rows;
    }

    /** Export currently filtered department projects as a professional government-style .xlsx report. */
    exportDeptProjectsXlsx(): void {
        const rows = this.opFilteredProjects;
        if (rows.length === 0) {
            alert('No projects to export with the current filters.');
            return;
        }

        const isOsdMin  = this.isMinisterOrOsd();
        const deptName  = this.selectedDept?.name || 'All Departments';
        const roleName  = this.isMinister() ? 'Minister'
                        : this.isOsd()      ? 'OSD'
                        : this.isApprover() ? 'Approver' : 'Operator';
        const now       = new Date();
        const dateStr   = now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
        const timeStr   = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
        const totalCost  = rows.reduce((s, p) => s + (p.costLakh  ?? 0), 0);
        const totalSpent = rows.reduce((s, p) => s + ((p as any).spentLakh ?? 0), 0);
        const u         = this.authService.currentUser();
        const userName  = (u as any)?.displayName || (u as any)?.email || 'System User';

        // ── Colour palette ──────────────────────────────────────────────────
        const C = {
            ORANGE:    'C2410C',   // header title bg
            NAVY:      '1E3A8A',   // subtitle / column-header bg
            WHITE:     'FFFFFF',
            LIGHT_BLU: 'DBEAFE',   // meta row bg
            ORANGE_LT: 'FFF7ED',   // stats row bg
            NAVY_HDR:  '1E3A8A',   // col-header bg
            EVEN:      'FFFFFF',
            ODD:       'F8FAFC',
            DELAYED:   'FEF2F2',   // light red row tint
            HM_ROW:    'FEFCE8',   // light yellow row tint
            BORDER:    'E2E8F0',
            GREEN_TXT: '166534',
            ORANGE_TXT:'C05621',
            RED_TXT:   'B91C1C',
            BLUE_TXT:  '1D4ED8',
            AMBER_TXT: 'B45309',
        };

        // ── Style builders ──────────────────────────────────────────────────
        const bdr = (rgb = C.BORDER) => ({
            top:    { style: 'thin', color: { rgb } },
            bottom: { style: 'thin', color: { rgb } },
            left:   { style: 'thin', color: { rgb } },
            right:  { style: 'thin', color: { rgb } },
        });

        const c = (v: any, s: object, t = 's', z?: string): any => {
            const obj: any = { v, t, s };
            if (z) obj.z = z;
            return obj;
        };

        const STYLES = {
            title: {
                font:      { bold: true, sz: 15, color: { rgb: C.WHITE }, name: 'Calibri' },
                fill:      { patternType: 'solid', fgColor: { rgb: C.ORANGE } },
                alignment: { horizontal: 'center', vertical: 'center' },
            },
            subtitle: {
                font:      { bold: true, sz: 11, color: { rgb: C.WHITE }, name: 'Calibri' },
                fill:      { patternType: 'solid', fgColor: { rgb: C.NAVY } },
                alignment: { horizontal: 'center', vertical: 'center' },
            },
            meta: {
                font:      { sz: 10, color: { rgb: '1E40AF' } },
                fill:      { patternType: 'solid', fgColor: { rgb: C.LIGHT_BLU } },
                alignment: { horizontal: 'center', vertical: 'center' },
            },
            stats: {
                font:      { bold: true, sz: 10, color: { rgb: '92400E' } },
                fill:      { patternType: 'solid', fgColor: { rgb: C.ORANGE_LT } },
                alignment: { horizontal: 'center', vertical: 'center' },
            },
            colHdr: {
                font:      { bold: true, sz: 10, color: { rgb: C.WHITE }, name: 'Calibri' },
                fill:      { patternType: 'solid', fgColor: { rgb: C.NAVY_HDR } },
                alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
                border:    bdr('93C5FD'),
            },
        };

        const dataStyle = (even: boolean, hm: boolean, delayed: boolean, align = 'left') => ({
            fill:      { patternType: 'solid', fgColor: { rgb: delayed ? C.DELAYED : hm ? C.HM_ROW : even ? C.EVEN : C.ODD } },
            alignment: { horizontal: align, vertical: 'center', wrapText: true },
            border:    bdr(),
            font:      { sz: 10, name: 'Calibri', bold: false, color: { rgb: '374151' } },
        });

        const statusFontColor = (st: string) =>
            st === 'Completed'   ? C.GREEN_TXT
          : st === 'In Progress' ? C.ORANGE_TXT
          : st === 'Stuck'       ? C.RED_TXT
          :                        C.BLUE_TXT;

        // ── Column definitions ──────────────────────────────────────────────
        interface ColDef { hdr: string; key: string; w: number; align?: string; numFmt?: string; osdOnly?: boolean; }

        const COLS: ColDef[] = [
            { hdr: '#',                  key: 'sl',        w: 5,  align: 'center' },
            { hdr: 'Project ID',         key: 'pid',       w: 16, align: 'center' },
            { hdr: 'Project Name',       key: 'name',      w: 40 },
            { hdr: 'Department',         key: 'dept',      w: 20 },
            { hdr: 'Scheme',             key: 'scheme',    w: 26 },
            { hdr: 'Block',              key: 'block',     w: 16 },
            { hdr: 'GP / NAC',           key: 'gp',        w: 18 },
            { hdr: 'Road Length (km)',   key: 'length',    w: 15, align: 'right', numFmt: '0.00' },
            { hdr: 'Executant Name',     key: 'exec',      w: 24 },
            { hdr: 'Contact No.',        key: 'contact',   w: 15 },
            { hdr: 'Est. Cost (₹L)',     key: 'cost',      w: 16, align: 'right', numFmt: '#,##0.00' },
            { hdr: 'Amount Spent (₹L)', key: 'spent',     w: 18, align: 'right', numFmt: '#,##0.00' },
            { hdr: 'Status',             key: 'status',    w: 14, align: 'center' },
            { hdr: 'HM Priority',        key: 'hm',        w: 14, align: 'center', osdOnly: true },
            { hdr: 'Recurring',          key: 'recurring', w: 14, align: 'center', osdOnly: true },
            { hdr: 'Delay Status',       key: 'delay',     w: 14, align: 'center', osdOnly: true },
            { hdr: 'Year',               key: 'year',      w: 8,  align: 'center' },
            { hdr: 'Last Updated',       key: 'updated',   w: 15, align: 'center' },
        ].filter(col => !col.osdOnly || isOsdMin);

        const ncols = COLS.length;
        const lastCol = String.fromCharCode(64 + ncols); // works up to 26 cols

        // ── Helper: full-width filler row ───────────────────────────────────
        const blank = (): any => ({ v: '', t: 's' });
        const fullRow = (v: string, style: object): any[] =>
            [c(v, style), ...Array.from({ length: ncols - 1 }, blank)];

        // ── AOA construction ────────────────────────────────────────────────
        const aoa: any[][] = [];

        // Row 0 – Report title
        aoa.push(fullRow('DHAMNAGAR DEVELOPMENT DASHBOARD', STYLES.title));

        // Row 1 – Department + Role
        aoa.push(fullRow(
            `Department: ${deptName}   |   User Role: ${roleName}`,
            STYLES.subtitle
        ));

        // Row 2 – Export date + generated by
        aoa.push(fullRow(
            `Export Date: ${dateStr}, ${timeStr}   |   Generated By: ${userName}`,
            STYLES.meta
        ));

        // Row 3 – Summary stats
        aoa.push(fullRow(
            `Total Projects: ${rows.length}   |   Total Est. Cost: ₹${totalCost.toFixed(2)} L   |   Total Amount Spent: ₹${totalSpent.toFixed(2)} L`,
            STYLES.stats
        ));

        // Row 4 – Spacer
        aoa.push(Array.from({ length: ncols }, blank));

        // Row 5 – Column headers
        aoa.push(COLS.map(col => c(col.hdr, STYLES.colHdr)));

        // ── Data rows (from AOA index 6 → Excel row 7) ─────────────────────
        rows.forEach((p, idx) => {
            const anyP     = p as any;
            const isHm     = !!anyP.hmPriority;
            const isDelay  = anyP.statusCategory === 'Delayed' || !!anyP.isDelayed;
            const isEven   = idx % 2 === 0;

            const getVal = (key: string): any => {
                switch (key) {
                    case 'sl':        return idx + 1;
                    case 'pid':       return anyP.projectId || anyP.id || '—';
                    case 'name':      return p.roadName ?? '—';
                    case 'dept':      return deptName;
                    case 'scheme':    return (p as any).schemeName ?? '—';
                    case 'block':     return anyP.block ?? anyP.division ?? p.constituency ?? '—';
                    case 'gp':        return anyP.gp ?? '—';
                    case 'length':    return (p.lengthKm != null && p.lengthKm !== undefined) ? Number(p.lengthKm) : null;
                    case 'exec':      return anyP.executantName || '—';
                    case 'contact':   return anyP.executantContact || '—';
                    case 'cost':      return Number(p.costLakh) || 0;
                    case 'spent':     return Number(anyP.spentLakh) || 0;
                    case 'status':    return p.status ?? 'Planned';
                    case 'hm':        return anyP.hmPriority ? 'Yes' : 'No';
                    case 'recurring': return anyP.recurringIntervention ? 'Yes' : 'No';
                    case 'delay':     return isDelay ? 'Delayed' : 'On Track';
                    case 'year':      return anyP.yearOfCreation ?? '—';
                    case 'updated':   return this.lastUpdatedOf(p);
                    default:          return '';
                }
            };

            const dataRow = COLS.map(col => {
                const v     = getVal(col.key);
                const isNum = col.numFmt !== undefined && v !== null && typeof v === 'number';
                const t     = isNum ? 'n' : 's';
                let   s     = dataStyle(isEven, isHm, isDelay, col.align ?? 'left');

                // Status: coloured text
                if (col.key === 'status' && typeof v === 'string') {
                    s = { ...s, font: { ...s.font, bold: true, color: { rgb: statusFontColor(v) } } };
                }
                // HM "Yes": amber bold
                if (col.key === 'hm' && v === 'Yes') {
                    s = { ...s, font: { ...s.font, bold: true, color: { rgb: C.AMBER_TXT } } };
                }
                // Delayed: red bold
                if (col.key === 'delay' && v === 'Delayed') {
                    s = { ...s, font: { ...s.font, bold: true, color: { rgb: C.RED_TXT } } };
                }
                // Null numeric → show dash
                if (isNum && v === null) {
                    return c('—', s);
                }
                return c(v ?? '', s, t, isNum ? col.numFmt : undefined);
            });

            aoa.push(dataRow);
        });

        // ── Sheet assembly ──────────────────────────────────────────────────
        const ws: any = XLSX.utils.aoa_to_sheet(aoa);

        // Column widths
        ws['!cols'] = COLS.map(col => ({ wch: col.w }));

        // Row heights
        ws['!rows'] = [
            { hpt: 30 }, // title
            { hpt: 24 }, // subtitle
            { hpt: 20 }, // meta
            { hpt: 20 }, // stats
            { hpt: 6  }, // spacer
            { hpt: 28 }, // column headers
            ...rows.map(() => ({ hpt: 20 })),
        ];

        // Merge header rows (span all columns)
        ws['!merges'] = [0, 1, 2, 3].map(r => ({ s: { r, c: 0 }, e: { r, c: ncols - 1 } }));

        // Freeze rows 1-6 so column headers stay visible when scrolling
        ws['!freeze'] = { xSplit: 0, ySplit: 6 };

        // Auto-filter on column header row (row 6 = index 5)
        ws['!autofilter'] = { ref: `A6:${lastCol}6` };

        // Landscape A4 print layout
        ws['!pageSetup'] = {
            orientation: 'landscape',
            paperSize:   9,   // A4
            fitToPage:   true,
            fitToWidth:  1,
            fitToHeight: 0,
            scale:       80,
        };

        // ── Workbook + download ─────────────────────────────────────────────
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, deptName.slice(0, 28));

        const stamp    = now.toISOString().split('T')[0];
        const fileBase = deptName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
        XLSX.writeFile(wb, `${fileBase}-report-${stamp}.xlsx`);
    }

    // Export menu toggle (now Excel-only; kept as a menu so future formats slot in)
    isExportMenuOpen = signal(false);
    toggleExportMenu(): void { this.isExportMenuOpen.update(v => !v); }
    closeExportMenu(): void { this.isExportMenuOpen.set(false); }

    // ── Project View/Edit modal (Minister = view-only, OSD = view + edit) ──
    /** Project currently rendered in the detail modal (null when modal is closed). */
    deptProjectDetail = signal<(SchemeProject & { schemeName: string; schemeShort: string }) | null>(null);
    /** Modal mode: false = view, true = edit (OSD-only). */
    isDeptProjectEditing = signal(false);
    /** In-flight save state for the modal footer button. */
    isSavingDeptProject  = signal(false);
    /** Save error text shown above the modal footer when present. */
    deptProjectSaveError = signal<string | null>(null);
    /** Form bound by the modal in edit mode (initialised on open). */
    deptProjectForm!: FormGroup;

    /** Resolve the live `projects` doc id for a row (CREATE path may have set `id`). */
    private resolveLiveProjectId(row: any): string | null {
        return row?.id ?? row?.projectId ?? null;
    }

    /** Open project details — navigates to the shared ProjectDetailComponent when a
     *  Firestore ID is available; falls back to the inline modal for static-only rows. */
    openDeptProjectModal(row: SchemeProject & { schemeName: string; schemeShort: string }): void {
        const firestoreId = this.resolveLiveProjectId(row);
        if (firestoreId) {
            this.router.navigate(['/dhamnagar-dashboard/project', firestoreId]);
            return;
        }
        // Fallback: inline modal for projects that exist only in static data (no Firestore doc)
        this.deptProjectDetail.set(row);
        this.isDeptProjectEditing.set(false);
        this.deptProjectSaveError.set(null);
        this.rebuildDeptProjectForm(row);
    }

    closeDeptProjectModal(): void {
        if (this.isSavingDeptProject()) return;
        this.deptProjectDetail.set(null);
        this.isDeptProjectEditing.set(false);
        this.deptProjectSaveError.set(null);
    }

    /** OSD: switch modal from view → edit. Minister: button is hidden, so this is OSD-only. */
    enterDeptProjectEditMode(): void {
        if (!this.isOsd()) return;
        const row = this.deptProjectDetail();
        if (!row) return;
        this.isDeptProjectEditing.set(true);
        this.deptProjectSaveError.set(null);
        this.rebuildDeptProjectForm(row);
    }

    cancelDeptProjectEdit(): void {
        if (this.isSavingDeptProject()) return;
        this.isDeptProjectEditing.set(false);
        const row = this.deptProjectDetail();
        if (row) this.rebuildDeptProjectForm(row);
    }

    /** Rebuild the modal form from a project row (used when opening or cancelling edits). */
    private rebuildDeptProjectForm(p: SchemeProject & { schemeName: string }): void {
        const anyP = p as any;
        this.deptProjectForm = this.fb.group({
            roadName:              [p.roadName ?? '',           [Validators.required, Validators.minLength(3)]],
            district:              [p.district ?? '',           Validators.required],
            division:              [p.division ?? '',           Validators.required],
            constituency:          [p.constituency ?? 'Dhamnagar', Validators.required],
            status:                [p.status ?? 'Planned',      Validators.required],
            lengthKm:              [p.lengthKm ?? null],
            costLakh:              [p.costLakh ?? null,         [Validators.required, Validators.min(0.01)]],
            spentLakh:             [(p as any).spentLakh ?? 0,  [Validators.min(0)]],
            hmPriority:            [!!anyP.hmPriority],
            recurringIntervention: [!!anyP.recurringIntervention],
        });
    }

    /** Mutual-exclusive HM/Recurring inside the modal — toggling one clears the other. */
    onModalHmPriorityChange(checked: boolean): void {
        this.deptProjectForm.patchValue({ hmPriority: checked }, { emitEvent: false });
        if (checked) this.deptProjectForm.patchValue({ recurringIntervention: false }, { emitEvent: false });
    }
    onModalRecurringChange(checked: boolean): void {
        this.deptProjectForm.patchValue({ recurringIntervention: checked }, { emitEvent: false });
        if (checked) this.deptProjectForm.patchValue({ hmPriority: false }, { emitEvent: false });
    }

    /**
     * OSD save → writes directly to the live `projects` doc (FirestoreProjectService
     * grants OSD direct-write authority). The existing live Firestore listener
     * re-emits the change, so HM Priority, Recurring Intervention, dashboard
     * counters, exports and other modules pick it up without a refresh.
     *
     * IMPORTANT — the in-memory `mergeFirestoreProjects()` reads canonical
     * Firestore field names (`name`, `loc`, `cost`, `spent`, `roadLength`,
     * `division`). We write BOTH the canonical names and the legacy
     * SchemeProject names so the table, exports, HM Priorities, Recurring
     * Intervention, and the main dashboard all round-trip the same values.
     */
    async saveDeptProjectChanges(): Promise<void> {
        if (!this.isOsd()) return;                                     // belt-and-braces guard
        const row = this.deptProjectDetail();
        if (!row) return;
        if (this.deptProjectForm.invalid) {
            this.deptProjectForm.markAllAsTouched();
            return;
        }

        const liveId = this.resolveLiveProjectId(row);
        if (!liveId) {
            this.deptProjectSaveError.set(
                'This project has no live Firestore document yet — it may still be awaiting approval.'
            );
            return;
        }

        const v = this.deptProjectForm.value;
        if (v.hmPriority && v.recurringIntervention) {
            this.deptProjectSaveError.set(
                'A project cannot be both HM Priority and Recurring Intervention. Pick one.'
            );
            return;
        }

        // Build the diff for the success toast before mutating anything.
        const anyRow = row as any;
        const before = {
            name:                  row.roadName,
            district:              row.district,
            division:              row.division,
            constituency:          row.constituency,
            status:                row.status ?? 'Planned',
            lengthKm:              row.lengthKm,
            costLakh:              row.costLakh,
            spentLakh:             (row as any).spentLakh ?? 0,
            hmPriority:            !!anyRow.hmPriority,
            recurringIntervention: !!anyRow.recurringIntervention,
        };
        const after = {
            name:                  v.roadName,
            district:              v.district,
            division:              v.division,
            constituency:          v.constituency,
            status:                v.status,
            lengthKm:              v.lengthKm !== null && v.lengthKm !== '' ? Number(v.lengthKm) : undefined,
            costLakh:              Number(v.costLakh) || 0,
            spentLakh:             Number(v.spentLakh) || 0,
            hmPriority:            !!v.hmPriority,
            recurringIntervention: !!v.recurringIntervention,
        };

        // Write payload — every value mirrored under both legacy + canonical keys
        // so the merge step reflashes the new state back into the in-memory table.
        const updates: Record<string, any> = {
            // Project name — canonical is `name`, legacy/scheme is `roadName`.
            name:                  after.name,
            roadName:              after.name,
            // Location — canonical is `loc`, legacy is `constituency`.
            loc:                   after.constituency,
            constituency:          after.constituency,
            // Block/division — canonical is `division`, legacy alias is `block`.
            division:              after.division,
            block:                 after.division,
            district:              after.district,
            status:                after.status,
            // Length — canonical is `roadLength`, scheme alias is `lengthKm`.
            roadLength:            after.lengthKm ?? null,
            lengthKm:              after.lengthKm ?? null,
            // Cost — canonical is `cost` (Lakhs in this codebase), alias is `costLakh`.
            cost:                  after.costLakh,
            costLakh:              after.costLakh,
            spent:                 after.spentLakh,
            spentLakh:             after.spentLakh,
            // HM / Recurring flags drive HM Priorities, Recurring Intervention,
            // hmStats, priorityProjects, and highVisibilityProjects across the app.
            hmPriority:            after.hmPriority,
            recurringIntervention: after.recurringIntervention,
        };

        this.isSavingDeptProject.set(true);
        this.deptProjectSaveError.set(null);
        try {
            await this.firestoreProjectService.updateProject(liveId, updates);

            // Optimistically refresh the in-memory row so the table updates
            // before the next Firestore snapshot lands.
            this.applyOsdEditOptimistically(liveId, after);

            // Build a human-readable diff for the toast
            const changes: string[] = [];
            if (before.name !== after.name)                 changes.push(`Name: "${before.name}" → "${after.name}"`);
            if (before.district !== after.district)         changes.push(`District: "${before.district}" → "${after.district}"`);
            if (before.division !== after.division)         changes.push(`Division: "${before.division}" → "${after.division}"`);
            if (before.constituency !== after.constituency) changes.push(`Constituency: "${before.constituency}" → "${after.constituency}"`);
            if (before.status !== after.status)             changes.push(`Status: ${before.status} → ${after.status}`);
            if ((before.lengthKm ?? 0) !== (after.lengthKm ?? 0)) changes.push(`Length: ${before.lengthKm ?? '—'} km → ${after.lengthKm ?? '—'} km`);
            if ((before.costLakh ?? 0) !== after.costLakh)  changes.push(`Cost: ₹${before.costLakh ?? 0} L → ₹${after.costLakh} L`);
            if ((before.spentLakh ?? 0) !== after.spentLakh) changes.push(`Spent: ₹${before.spentLakh ?? 0} L → ₹${after.spentLakh} L`);
            if (before.hmPriority !== after.hmPriority)     changes.push(`HM Priority: ${before.hmPriority ? 'Yes' : 'No'} → ${after.hmPriority ? 'Yes' : 'No'}`);
            if (before.recurringIntervention !== after.recurringIntervention) changes.push(`Recurring: ${before.recurringIntervention ? 'Yes' : 'No'} → ${after.recurringIntervention ? 'Yes' : 'No'}`);

            this.showUpdateToast(`"${after.name}" updated`, changes.length ? changes : ['No field changes detected']);

            this.deptProjectDetail.set(null);
            this.isDeptProjectEditing.set(false);
        } catch (err: any) {
            console.error('[dept-modal] OSD save failed:', err);
            this.deptProjectSaveError.set(err?.message ?? 'Could not save changes. Please try again.');
        } finally {
            this.isSavingDeptProject.set(false);
        }
    }

    /**
     * Apply an OSD edit to the in-memory scheme tree right away — so the
     * department table reflects the change without waiting for the
     * Firestore snapshot. The live stream will overwrite this shortly with
     * the authoritative server state.
     */
    private applyOsdEditOptimistically(
        liveId: string,
        after: { name: string; district: string; division: string; constituency: string;
                 status: string; lengthKm?: number; costLakh: number; spentLakh: number;
                 hmPriority: boolean; recurringIntervention: boolean }
    ): void {
        if (!this.selectedDept) return;
        for (const scheme of this.selectedDept.schemes) {
            const list = scheme.projectList;
            if (!list) continue;
            const idx = list.findIndex(p => p.id === liveId);
            if (idx === -1) continue;
            list[idx] = {
                ...list[idx],
                roadName:              after.name,
                district:              after.district,
                division:              after.division,
                constituency:          after.constituency,
                status:                after.status as SchemeProject['status'],
                lengthKm:              after.lengthKm,
                costLakh:              after.costLakh,
                spentLakh:             after.spentLakh,
                hmPriority:            after.hmPriority,
                recurringIntervention: after.recurringIntervention,
            };
            const stats = this.calculateSchemeStats(list);
            scheme.projects   = stats.totalProjects;
            scheme.totalCost  = stats.totalCost;
            scheme.spent      = stats.spent;
            scheme.completed  = stats.completed;
            scheme.inProgress = stats.inProgress;
            scheme.stuck      = stats.stuck;
            scheme.planned    = stats.planned;
            // Force Angular to re-read selectedDept (referential identity check)
            this.selectedDept = this.departments.find(d => d.name === this.selectedDept!.name) ?? this.selectedDept;
            return;
        }
    }

    // ── Project-table pagination ────────────────────────────────────────
    /** Current 1-based page index. Resets to 1 when filters change. */
    opPage = signal(1);
    /** Visible rows per page. */
    opPageSize = signal<number>(25);

    /** Total pages for the current filter set. */
    get opTotalPages(): number {
        return Math.max(1, Math.ceil(this.opFilteredProjects.length / this.opPageSize()));
    }

    /** Slice of opFilteredProjects for the current page. Clamps the page index
     *  so changing filters can never leave the user on an empty page. */
    get opPagedProjects(): Array<SchemeProject & { schemeName: string; schemeShort: string }> {
        const all = this.opFilteredProjects;
        const max = Math.max(1, Math.ceil(all.length / this.opPageSize()));
        if (this.opPage() > max) {
            // schedule a microtask reset; reading the getter shouldn't mutate state synchronously
            queueMicrotask(() => this.opPage.set(max));
        }
        const safePage = Math.min(this.opPage(), max);
        const start = (safePage - 1) * this.opPageSize();
        return all.slice(start, start + this.opPageSize());
    }

    /** SL number to render for a row index on the current page. */
    opSlForIndex(i: number): number {
        return (this.opPage() - 1) * this.opPageSize() + i + 1;
    }

    opGoToPage(p: number): void {
        const max = this.opTotalPages;
        this.opPage.set(Math.max(1, Math.min(p, max)));
    }
    opPrevPage(): void { this.opGoToPage(this.opPage() - 1); }
    opNextPage(): void { this.opGoToPage(this.opPage() + 1); }
    opSetPageSize(size: number | string): void {
        const n = Number(size) || 25;
        this.opPageSize.set(n);
        this.opPage.set(1);
    }

    /** Range string for the pagination footer ("1–25 of 132"). */
    get opPageRange(): string {
        const total = this.opFilteredProjects.length;
        if (total === 0) return '0 of 0';
        const start = (this.opPage() - 1) * this.opPageSize() + 1;
        const end   = Math.min(start + this.opPageSize() - 1, total);
        return `${start}–${end} of ${total}`;
    }

    /**
     * Derive a short form / code from a scheme name.
     * Prefers the parenthesised acronym (e.g. "Central Road and Infrastructure Fund (CRIF)" → "CRIF"),
     * otherwise falls back to the initials of the first 4 capitalised words.
     */
    schemeShortForm(name: string): string {
        if (!name) return '';
        const paren = name.match(/\(([A-Z][A-Z0-9]{1,8})\)/);
        if (paren) return paren[1];
        const words = name.replace(/\([^)]*\)/g, '').split(/\s+/).filter(w => /^[A-Z]/.test(w));
        if (words.length >= 2) return words.slice(0, 4).map(w => w[0]).join('');
        return name.slice(0, 6).toUpperCase();
    }

    /** Coerce Firestore Timestamp / Date / string / number → epoch ms. */
    private tsMs(t: any): number {
        if (!t) return 0;
        if (typeof t?.toDate === 'function') return t.toDate().getTime();
        if (typeof t?.seconds === 'number')   return t.seconds * 1000;
        if (t instanceof Date)                return t.getTime();
        if (typeof t === 'number')            return t;
        if (typeof t === 'string')            return Date.parse(t) || 0;
        return 0;
    }

    /** Format an `updatedAt`-style value for the table. */
    formatTs(t: any): string {
        const ms = this.tsMs(t);
        if (!ms) return '—';
        const d = new Date(ms);
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
    }

    /**
     * Resolve a "last updated" date from whatever the underlying row carries.
     * SchemeProject doesn't declare these fields, but rows merged in from
     * Firestore (`Project`) often do — accept any of the common shapes.
     */
    lastUpdatedOf(row: any): string {
        return this.formatTs(row?.updatedAt ?? row?.lastUpdated ?? row?.createdAt);
    }

    // ── APPROVER DEPARTMENT VIEW ───────────────────────────────────────
    /** Live stream of pending approval requests this approver can act on. */
    approverPendingRequests = signal<ApprovalRequest[]>([]);
    /** Map of project id → its latest pending request (so the table can show "PENDING" / open the modal). */
    pendingByProjectId = computed<Map<string, ApprovalRequest>>(() => {
        const map = new Map<string, ApprovalRequest>();
        for (const r of this.approverPendingRequests()) {
            if (r.status !== 'PENDING') continue;
            if (r.targetDocId) map.set(r.targetDocId, r);
        }
        return map;
    });

    /** uid → displayName for the approver modal meta strip. */
    private approverUserNames = signal<Map<string, string>>(new Map());

    /** Counters for the four summary cards on the approver dept view. */
    approverDeptCounts = computed(() => {
        const projects = this.opAllProjects;            // current dept's projects (already filtered by selectedDept)
        const total    = projects.length;
        let pending = 0;
        const pendingMap = this.pendingByProjectId();
        for (const p of projects) {
            if (p.id && pendingMap.has(p.id)) pending++;
        }
        const approved = projects.filter(p => p.status === 'Completed').length;
        const rejected = projects.filter(p => p.status === 'Stuck').length;
        return { total, pending, approved, rejected };
    });

    /** Approval-status pill for a single row. */
    approvalStatusOf(row: SchemeProject & { id?: string }): { label: string; cls: string } {
        if (row.id && this.pendingByProjectId().has(row.id)) {
            return { label: 'Pending', cls: 'op-pill op-pending' };
        }
        // No open request → show project's live state mapped to an approval-style label
        switch (row.status) {
            case 'Completed':   return { label: 'Approved', cls: 'op-pill op-completed' };
            case 'Stuck':       return { label: 'Rejected', cls: 'op-pill op-stuck' };
            case 'In Progress': return { label: 'Active',   cls: 'op-pill op-inprogress' };
            default:            return { label: '—',        cls: 'op-pill op-planned' };
        }
    }

    // ── Approver view modal state (re-uses CompareChangesComponent) ──
    approverModalOpen      = signal(false);
    approverModalBefore    = signal<any>(null);
    approverModalAfter     = signal<any>(null);
    approverModalMeta      = signal<any>(null);
    approverModalReadOnly  = signal(false);
    approverModalRequestId = signal<string | null>(null);
    approverSubmitting     = signal(false);

    private toDate(t: any): Date | null {
        if (!t) return null;
        if (t instanceof Date) return t;
        if (typeof t?.toDate === 'function') return t.toDate();
        if (typeof t?.seconds === 'number')   return new Date(t.seconds * 1000);
        if (typeof t === 'number')            return new Date(t);
        if (typeof t === 'string')            return new Date(t);
        return null;
    }

    /** View a project — opens the Compare Changes modal. */
    async approverViewProject(row: SchemeProject & { id?: string; schemeName: string }): Promise<void> {
        // Look up an open pending request for this project (if any)
        const pendingReq = row.id ? this.pendingByProjectId().get(row.id) : undefined;

        if (pendingReq) {
            // ── Live approval flow ──
            const operatorName = this.approverUserNames().get(pendingReq.operatorUid)
                                 || pendingReq.operatorUid.slice(0, 8) + '…';
            this.approverModalBefore.set((pendingReq as any).oldData ?? null);
            this.approverModalAfter.set((pendingReq as any).newData ?? pendingReq.payload ?? null);
            this.approverModalMeta.set({
                operator:  operatorName,
                operation: pendingReq.operation,
                date:      this.toDate((pendingReq as any).submittedAt ?? pendingReq.createdAt) || undefined,
                status:    pendingReq.status,
                remarks:   pendingReq.remarks,
            });
            this.approverModalRequestId.set(pendingReq.id ?? null);
            this.approverModalReadOnly.set(false);   // approve / reject enabled
        } else {
            // ── Read-only project snapshot ──
            this.approverModalBefore.set(null);
            this.approverModalAfter.set({
                name:                  row.roadName,
                dept:                  this.selectedDept?.name,
                scheme:                row.schemeName,
                district:              row.district,
                division:              row.division,
                loc:                   row.constituency,
                lengthKm:              row.lengthKm,
                cost:                  row.costLakh,
                spent:                 row.spentLakh,
                physical:              (row as any).physical ?? 0,
                status:                row.status,
                hmPriority:            row.hmPriority,
                recurringIntervention: row.recurringIntervention,
            });
            this.approverModalMeta.set({
                operator:  '—',
                operation: 'view',
                date:      undefined,
                status:    row.status,
                remarks:   '',
            });
            this.approverModalRequestId.set(null);
            this.approverModalReadOnly.set(true);
        }
        this.approverModalOpen.set(true);
    }

    closeApproverModal(): void {
        this.approverModalOpen.set(false);
        this.approverModalRequestId.set(null);
    }

    async approverApprove(
        payload: string | { remarks: string; editedPayload?: Record<string, any> }
    ): Promise<void> {
        const id = this.approverModalRequestId();
        if (!id) return;
        const remarks = typeof payload === 'string' ? payload : (payload?.remarks ?? '');
        const edits   = typeof payload === 'string' ? null   : (payload?.editedPayload ?? null);
        this.approverSubmitting.set(true);
        try {
            await this.approvalService.approveRequest(id, remarks, edits);
            this.closeApproverModal();
        } catch (err) {
            console.error('[approver-dept] approve failed:', err);
        } finally {
            this.approverSubmitting.set(false);
        }
    }

    async approverReject(remarks: string): Promise<void> {
        const id = this.approverModalRequestId();
        if (!id) return;
        this.approverSubmitting.set(true);
        try {
            await this.approvalService.rejectRequest(id, remarks ?? '');
            this.closeApproverModal();
        } catch (err) {
            console.error('[approver-dept] reject failed:', err);
        } finally {
            this.approverSubmitting.set(false);
        }
    }

    /**
     * Operator focus-edit mode: when true, the scheme drawer hides the project
     * list and only renders the edit form for the single project the operator
     * picked from the table.
     */
    opFocusEditMode = signal(false);

    /** Open the existing edit drawer in operator focus-edit mode (form only). */
    opEditProject(row: SchemeProject & { schemeName: string }): void {
        if (!this.selectedDept) return;
        const parent = this.selectedDept.schemes.find(s => s.name === row.schemeName);
        if (!parent) return;
        this.opFocusEditMode.set(true);
        // Open the drawer synchronously, then open the form straight after so
        // resetFormState() (called inside openSchemeDrawer) doesn't clobber it.
        this.openSchemeDrawer(parent);
        this.openEditProjectForm(row);
    }

    /** Cleared whenever the drawer closes (or after save / cancel). */
    clearOpFocusEdit(): void {
        this.opFocusEditMode.set(false);
    }

    /** Quick-action wrapper buttons in the table action column. */
    opUpdateProgress(row: SchemeProject & { schemeName: string }): void {
        this.opEditProject(row);
    }
    opUploadPhoto(row: SchemeProject & { schemeName: string }): void {
        this.opEditProject(row); // photo upload is inside the edit drawer
    }
    opSubmitForApproval(row: SchemeProject & { schemeName: string }): void {
        // Re-saving as an operator pushes to approval queue (FirestoreProjectService.updateProject does this)
        this.opEditProject(row);
    }

    /** Pretty status pill class for the table. */
    opStatusClass(status: string | undefined): string {
        switch (status) {
            case 'Completed':   return 'op-pill op-completed';
            case 'In Progress': return 'op-pill op-inprogress';
            case 'Stuck':       return 'op-pill op-stuck';
            case 'Planned':     return 'op-pill op-planned';
            default:            return 'op-pill op-planned';
        }
    }

    opProgressOf(row: any): number {
        return Math.max(0, Math.min(100, Number(row.physical ?? 0)));
    }

    opResetFilters(): void {
        this.opTableSearch = '';
        this.opTableStatus = '';
        this.opTableScheme = '';
        this.opTableProgressBand = '';
        this.opTableSort = 'updated-desc';
        this.opTableBlock = '';
        this.opTableGp = '';
        this.opTableExecutant = '';
        this.opSelectedSchemes.set(new Set());
    }

    // ── Mobile app shell (≤767px) ─────────────────────────────────────
    mobileProjView = signal<'list' | 'cards'>('list');
    mobileFilterSheetOpen = signal(false);

    openMobileFilterSheet(): void {
        this.mobileFilterSheetOpen.set(true);
    }

    closeMobileFilterSheet(): void {
        this.mobileFilterSheetOpen.set(false);
        this.closeOpSchemesMenu();
    }

    setMobileExecutant(value: string): void {
        this.opTableExecutant = value;
    }

    applyMobileFilters(): void {
        this.closeMobileFilterSheet();
    }

    /** Filters configured in the bottom sheet (not the two primary rows). */
    mobileSheetFilterCount(): number {
        let n = 0;
        if (this.opTableExecutant) n++;
        if (this.isMinisterOrOsd()) {
            if (this.opSelectedSchemes().size > 0) n++;
        } else {
            if (this.opTableProgressBand) n++;
            if (this.opTableSort !== 'updated-desc') n++;
        }
        return n;
    }

    mobileLocationOf(row: SchemeProject & { schemeName: string }): string {
        const anyP = row as any;
        if (this.isMinisterOrOsd()) {
            return anyP.gp || anyP.division || '—';
        }
        return anyP.division || anyP.district || '—';
    }

    mobileUpdatedLabel(row: any): string {
        const ms = this.tsMs(row?.updatedAt ?? row?.lastUpdated ?? row?.createdAt);
        if (!ms) return 'Not updated';
        const d = new Date(ms);
        const now = new Date();
        const sameDay = d.toDateString() === now.toDateString();
        if (sameDay) return 'Updated today';
        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);
        if (d.toDateString() === yesterday.toDateString()) return 'Updated yesterday';
        return `Updated ${d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}`;
    }

    mobileStatusLabel(status: string | undefined): string {
        const s = status || 'Planned';
        if (s === 'In Progress') return 'Active';
        return s;
    }

    hasActiveMobileFilters(): boolean {
        return !!(
            this.opTableSearch ||
            this.opTableStatus ||
            this.opTableScheme ||
            this.opTableProgressBand ||
            this.opTableBlock ||
            this.opTableGp ||
            this.opTableExecutant ||
            this.opSelectedSchemes().size > 0
        );
    }

    // ── Operator delete confirmation ──────────────────────────────────
    opDeleteTarget = signal<(SchemeProject & { schemeName: string; schemeShort?: string }) | null>(null);
    opIsSubmittingDelete = signal(false);
    opDeleteError = signal<string | null>(null);
    opDeleteSubmitted = signal(false);   // shows "sent for approval" success state

    promptOpDelete(row: SchemeProject & { schemeName: string }): void {
        this.opDeleteError.set(null);
        this.opDeleteSubmitted.set(false);
        this.opDeleteTarget.set(row as any);
    }

    cancelOpDelete(): void {
        if (this.opIsSubmittingDelete()) return;
        this.opDeleteTarget.set(null);
        this.opDeleteError.set(null);
        this.opDeleteSubmitted.set(false);
    }

    /**
     * Operator-side "Send for Approval" — calls `deleteProject` which, when
     * invoked by an operator, enqueues a `delete` request in the approval
     * queue (department-routed) instead of touching the live `projects` doc.
     */
    async confirmOpDelete(): Promise<void> {
        const row = this.opDeleteTarget();
        if (!row) return;
        this.opIsSubmittingDelete.set(true);
        this.opDeleteError.set(null);
        try {
            if (row.id) {
                await this.firestoreProjectService.deleteProject(row.id);
                this.opDeleteSubmitted.set(true);
                // auto-close after a moment
                setTimeout(() => this.cancelOpDelete(), 1500);
            } else {
                // Legacy in-memory entry only — no Firestore doc to queue against.
                this.opDeleteError.set(
                    'This project is not yet synced to the live database. ' +
                    'Save any edit first so it can be tracked, then re-submit the delete.'
                );
            }
        } catch (err: any) {
            console.error('[op-delete] queue failed:', err);
            this.opDeleteError.set(err?.message ?? 'Could not submit delete request. Please try again.');
        } finally {
            this.opIsSubmittingDelete.set(false);
        }
    }

    /** Scheme card click → open drawer */
    openSchemeDrawer(scheme: SchemeCard): void {
        this.drawerScheme = scheme;
        this.drawerDept   = this.selectedDept;
        this.drawerOpen   = true;
        this.resetFormState();
    }

    /** Open drawer AND immediately show the Add Project form */
    openSchemeDrawerWithAdd(scheme: SchemeCard, event: Event): void {
        event.stopPropagation();
        this.drawerScheme = scheme;
        this.drawerDept   = this.selectedDept;
        this.drawerOpen   = true;
        this.openAddProjectForm();
    }

    closeDrawer(): void {
        this.drawerOpen = false;
        this.selectedSlNos.set(new Set());
        this.showBulkDeleteConfirm.set(false);
        this.resetFormState();
        this.clearOpFocusEdit();
        setTimeout(() => { this.drawerScheme = null; this.drawerDept = null; }, 300);
    }

    private resetFormState(): void {
        this.clearAllStagedPhotos();
        this.showAddForm = false;
        this.formMode = 'add';
        this.editingSlNo = null;
        this.editingProjectId = null;
        this.editingProjectPhotos.set([]);
        this.deletingSlNo = null;
    }

    /** Close drawer / project-detail modal when ESC pressed */
    @HostListener('document:keydown.escape')
    onEscape() {
        if (this.drawerOpen) this.closeDrawer();
        if (this.deptProjectDetail()) this.closeDeptProjectModal();
        if (this.isOpSchemesMenuOpen()) this.closeOpSchemesMenu();
    }

    /** Close the OSD multi-select schemes dropdown when clicking outside it. */
    @HostListener('document:click', ['$event'])
    onDocumentClick(ev: MouseEvent) {
        if (!this.isOpSchemesMenuOpen()) return;
        const target = ev.target as HTMLElement | null;
        if (target && target.closest('.op-multi-schemes')) return;
        this.closeOpSchemesMenu();
    }

    /** Totals for the drawer footer */
    get drawerTotalLength(): number {
        return this.drawerScheme?.projectList?.reduce((s, p) => s + (p.lengthKm ?? 0), 0) ?? 0;
    }
    get drawerTotalCostLakh(): number {
        return this.drawerScheme?.projectList?.reduce((s, p) => s + p.costLakh, 0) ?? 0;
    }

    getDeptBg(color: string): string {
        if (!color) return 'dept-blue';
        const c = color.toLowerCase();
        // Map hex codes from DepartmentSchemesService to CSS classes
        if (c === '#ef4444' || c === '#dc2626') return 'dept-red';
        if (c === '#3b82f6' || c === '#2563eb') return 'dept-blue';
        if (c === '#a855f7') return 'dept-purple';
        if (c === '#f59e0b' || c === '#d97706') return 'dept-amber';
        if (c === '#06b6d4' || c === '#0ea5e9' || c === '#0891b2') return 'dept-cyan';
        if (c === '#8b5cf6' || c === '#7c3aed') return 'dept-violet';
        if (c === '#6366f1' || c === '#4f46e5') return 'dept-indigo';
        if (c === '#10b981' || c === '#16a34a') return 'dept-green';
        if (c === '#f97316' || c === '#ea580c') return 'dept-orange';
        if (c === '#14b8a6' || c === '#0d9488') return 'dept-teal';
        if (c === '#ec4899') return 'dept-pink';
        if (c === '#eab308') return 'dept-yellow';
        if (c === '#65a30d') return 'dept-lime';

        // Fallback for simple name strings if any
        const map: Record<string, string> = {
            red: 'dept-red', blue: 'dept-blue', purple: 'dept-purple',
            amber: 'dept-amber', cyan: 'dept-cyan', violet: 'dept-violet',
            green: 'dept-green', orange: 'dept-orange', teal: 'dept-teal',
            pink: 'dept-pink', indigo: 'dept-indigo', yellow: 'dept-yellow'
        };
        return map[c] ?? 'dept-blue';
    }

    getStatusClass(status?: string): string {
        switch (status) {
            case 'Completed':   return 'status-completed';
            case 'In Progress': return 'status-inprogress';
            case 'Stuck':       return 'status-stuck';
            default:            return 'status-planned';
        }
    }

    /** Add-project form */
    openAddProjectForm(): void {
        this.formMode = 'add';
        this.editingSlNo = null;
        this.drawerHasLength = this.drawerScheme?.roadLength !== undefined;
        const nextSlNo = (this.drawerScheme?.projectList?.length ?? 0) + 1;
        this.addProjectForm = this.fb.group({
            slNo:                 [nextSlNo, [Validators.required, Validators.min(1)]],
            roadName:             ['', [Validators.required, Validators.minLength(3)]],
            district:             ['Bhadrak', Validators.required],
            division:             ['', Validators.required],
            constituency:         ['Dhamnagar'],
            gp:                   [''],
            yearOfCreation:       ['2025-26'],
            lengthKm:             [null],
            costLakh:             [null, [Validators.required, Validators.min(0.01)]],
            spentLakh:            [null, [Validators.min(0)]],
            status:               ['Planned', Validators.required],
            executantName:        [''],
            executantContact:     [''],
            hmPriority:           [false],
            recurringIntervention:[false],
            isDelayed:            [false],
        });
        this.hmPriorityFlag = false;
        this.recurringFlag  = false;
        this.delayedFlag    = false;
        this.showAddForm = true;
    }

    openEditProjectForm(proj: SchemeProject): void {
        this.formMode = 'edit';
        this.editingSlNo = proj.slNo;
        this.editingProjectId = proj.id ?? null;
        this.editingProjectPhotos.set(proj.photos ?? []);
        this.drawerHasLength = this.drawerScheme?.roadLength !== undefined;
        const anyP = proj as any;
        this.addProjectForm = this.fb.group({
            slNo:                 [proj.slNo, [Validators.required, Validators.min(1)]],
            roadName:             [proj.roadName, [Validators.required, Validators.minLength(3)]],
            district:             [proj.district, Validators.required],
            division:             [proj.division, Validators.required],
            constituency:         [proj.constituency],
            gp:                   [anyP.gp ?? ''],
            yearOfCreation:       [anyP.yearOfCreation ?? ''],
            lengthKm:             [proj.lengthKm ?? null],
            costLakh:             [proj.costLakh, [Validators.required, Validators.min(0.01)]],
            spentLakh:            [proj.spentLakh ?? null, [Validators.min(0)]],
            status:               [proj.status ?? 'Planned', Validators.required],
            executantName:        [anyP.executantName ?? anyP.executant?.name ?? ''],
            executantContact:     [anyP.executantContact ?? anyP.executant?.contact ?? ''],
            hmPriority:           [proj.hmPriority ?? false],
            recurringIntervention:[proj.recurringIntervention ?? false],
            isDelayed:            [anyP.isDelayed ?? false],
        });
        this.hmPriorityFlag = proj.hmPriority ?? false;
        this.recurringFlag  = proj.recurringIntervention ?? false;
        this.delayedFlag    = anyP.isDelayed ?? false;
        this.showAddForm = true;
    }

    closeAddProjectForm(): void {
        this.clearAllStagedPhotos();
        this.showAddForm = false;
        this.formMode = 'add';
        this.editingSlNo = null;
        this.editingProjectId = null;
        this.editingProjectPhotos.set([]);
    }

    private clearAllStagedPhotos(): void {
        [...this.stagedPhotosBefore(), ...this.stagedPhotosDuring(), ...this.stagedPhotosAfter()]
            .forEach(e => URL.revokeObjectURL(e.previewUrl));
        this.stagedPhotosBefore.set([]);
        this.stagedPhotosDuring.set([]);
        this.stagedPhotosAfter.set([]);
        // legacy flat list (kept for edit-mode compat)
        this.stagedPhotoEntries().forEach(e => URL.revokeObjectURL(e.previewUrl));
        this.stagedPhotoEntries.set([]);
    }

    /** Mutual-exclusive: selecting HM Priority clears Recurring */
    onHmPriorityChange(checked: boolean): void {
        this.hmPriorityFlag = checked;
        this.addProjectForm.patchValue({ hmPriority: checked }, { emitEvent: false });
        if (checked) {
            this.recurringFlag = false;
            this.addProjectForm.patchValue({ recurringIntervention: false }, { emitEvent: false });
        }
    }

    /** Mutual-exclusive: selecting Recurring clears HM Priority */
    onRecurringChange(checked: boolean): void {
        this.recurringFlag = checked;
        this.addProjectForm.patchValue({ recurringIntervention: checked }, { emitEvent: false });
        if (checked) {
            this.hmPriorityFlag = false;
            this.addProjectForm.patchValue({ hmPriority: false }, { emitEvent: false });
        }
    }

    onDelayedChange(checked: boolean): void {
        this.delayedFlag = checked;
        this.addProjectForm.patchValue({ isDelayed: checked }, { emitEvent: false });
    }

    /** Handle categorised photo selection (Before / During / After) */
    onCreatePhotoCategorySelected(category: 'before' | 'during' | 'after', event: Event): void {
        const input = event.target as HTMLInputElement;
        const files = Array.from(input.files ?? []);
        input.value = '';
        const sig = category === 'before' ? this.stagedPhotosBefore
                  : category === 'during' ? this.stagedPhotosDuring
                  : this.stagedPhotosAfter;
        const remaining = 5 - sig().length;
        if (remaining <= 0) return;
        const valid = files.slice(0, remaining).filter(f =>
            ['image/jpeg', 'image/png'].includes(f.type) && f.size <= 2 * 1024 * 1024
        );
        sig.update(list => [...list, ...valid.map(file => ({ file, previewUrl: URL.createObjectURL(file) }))]);
    }

    removeStagedPhotoCategory(category: 'before' | 'during' | 'after', index: number): void {
        const sig = category === 'before' ? this.stagedPhotosBefore
                  : category === 'during' ? this.stagedPhotosDuring
                  : this.stagedPhotosAfter;
        const entries = sig();
        if (entries[index]) URL.revokeObjectURL(entries[index].previewUrl);
        sig.update(list => list.filter((_, i) => i !== index));
    }

    /** Legacy flat-list handler kept for any existing code paths */
    onCreatePhotoSelected(event: Event): void {
        this.onCreatePhotoCategorySelected('before', event);
    }

    removeStagedPhoto(index: number): void {
        this.removeStagedPhotoCategory('before', index);
    }

    async onDeptProjectPhotosChanged(newPhotos: string[]): Promise<void> {
        if (!this.editingProjectId) return;
        this.editingProjectPhotos.set(newPhotos);
        await this.firestoreProjectService.updateProject(this.editingProjectId, { photos: newPhotos })
            .catch(err => console.error('Failed to save photo URLs:', err));
    }

    promptDeleteProject(slNo: number): void {
        this.deletingSlNo = slNo;
        this.showAddForm = false; // close any open form
    }

    cancelDeleteProject(): void {
        this.deletingSlNo = null;
    }

    confirmDeleteProject(slNo: number): void {
        // If this project was stored in the main Firestore projects collection (has an id),
        // delete it there too — otherwise it will be re-injected by mergeFirestoreProjects on
        // the next stream emission.
        const fsId = this.drawerScheme?.projectList?.find(p => p.slNo === slNo)?.id;
        if (fsId) {
            this.firestoreProjectService.deleteProject(fsId)
                .catch(err => console.error('Failed to delete project from Firestore projects:', err));
        }

        this.deptService.deleteProjectFromScheme(
            this.drawerDept!.name,
            this.drawerScheme!.name,
            slNo
        );
        this.drawerScheme = this.drawerDept!.schemes.find(s => s.name === this.drawerScheme!.name) ?? this.drawerScheme;
        this.deletingSlNo = null;
        this.syncDeptToFirestore();
    }

    changeProjectStatus(proj: SchemeProject, status: string): void {
        // Persist to projects collection if this is a Firestore-backed project
        if (proj.id) {
            this.firestoreProjectService.updateProject(proj.id, { status: status as Project['status'] })
                .catch(err => console.error('Failed to persist status change to Firestore:', err));
        }
        this.deptService.updateProjectInScheme(
            this.drawerDept!.name,
            this.drawerScheme!.name,
            proj.slNo,
            { status: status as SchemeProject['status'] }
        );
        this.drawerScheme = this.drawerDept!.schemes.find(s => s.name === this.drawerScheme!.name) ?? this.drawerScheme;
        this.syncDeptToFirestore();
    }

    private showUpdateToast(title: string, lines: string[]): void {
        if (this.toastTimer) clearTimeout(this.toastTimer);
        this.updateToast.set({ title, lines });
        this.toastTimer = setTimeout(() => this.updateToast.set(null), 5000);
    }

    async submitAddProject(): Promise<void> {
        if (this.addProjectForm.invalid) {
            this.addProjectForm.markAllAsTouched();
            return;
        }
        if (this.isSaving()) return;
        this.isSaving.set(true);

        try {
            const v = this.addProjectForm.value;
            const hmPriority: boolean = v.hmPriority ?? false;
            const recurringIntervention: boolean = v.recurringIntervention ?? false;
            const today = new Date().toISOString().split('T')[0];
            const fiscalYearEnd = today < `${new Date().getFullYear()}-04-01`
                ? `${new Date().getFullYear()}-03-31`
                : `${new Date().getFullYear() + 1}-03-31`;

            if (this.formMode === 'edit') {
                /* ── EDIT MODE ─────────────────────────────────────────── */
                const data: Omit<SchemeProject, 'slNo'> = {
                    district:             v.district,
                    division:             v.division,
                    constituency:         v.constituency,
                    roadName:             v.roadName,
                    costLakh:             Number(v.costLakh),
                    spentLakh:            v.spentLakh != null ? Number(v.spentLakh) : undefined,
                    lengthKm:             (this.drawerHasLength && v.lengthKm != null) ? Number(v.lengthKm) : undefined,
                    status:               v.status,
                    hmPriority,
                    recurringIntervention,
                };

                if (this.editingProjectId) {
                    await this.firestoreProjectService.updateProject(this.editingProjectId, {
                        name:   v.roadName,
                        loc:    v.constituency,
                        cost:   Number(v.costLakh),
                        spent:  v.spentLakh != null ? Number(v.spentLakh) : 0,
                        status: v.status,
                        hmPriority,
                        recurringIntervention,
                    }).catch(err => console.error('Failed to update project:', err));
                } else if (hmPriority || recurringIntervention) {
                    // Editing a dept project that had no Firestore record yet — create one now
                    const newId = await this.firestoreProjectService.createProject({
                        name:                 v.roadName,
                        dept:                 this.drawerDept!.shortName,
                        loc:                  v.constituency,
                        cost:                 Number(v.costLakh),
                        spent:                v.spentLakh != null ? Number(v.spentLakh) : 0,
                        physical:             0,
                        status:               v.status,
                        start:                today,
                        end:                  fiscalYearEnd,
                        priority:             false,
                        hmPriority,
                        recurringIntervention,
                        remarks:              '',
                        scheme:               this.drawerScheme!.name,
                    }).catch(err => { console.error('Failed to create project:', err); return null; });
                    if (newId) { this.editingProjectId = newId; data.id = newId; }
                }

                // Build human-readable diff for toast
                const oldProj = this.drawerScheme?.projectList?.find(p => p.slNo === this.editingSlNo);
                const changes: string[] = [];
                if (oldProj) {
                    if (oldProj.roadName !== v.roadName) changes.push(`Name: "${oldProj.roadName}" → "${v.roadName}"`);
                    if (oldProj.district !== v.district) changes.push(`District: "${oldProj.district}" → "${v.district}"`);
                    if (oldProj.division !== v.division) changes.push(`Division: "${oldProj.division}" → "${v.division}"`);
                    if (oldProj.constituency !== v.constituency) changes.push(`Constituency: "${oldProj.constituency}" → "${v.constituency}"`);
                    if ((oldProj.status ?? 'Planned') !== v.status) changes.push(`Status: ${oldProj.status ?? 'Planned'} → ${v.status}`);
                    if (oldProj.costLakh !== Number(v.costLakh)) changes.push(`Cost: ₹${oldProj.costLakh} L → ₹${v.costLakh} L`);
                    if ((oldProj.spentLakh ?? 0) !== (v.spentLakh != null ? Number(v.spentLakh) : 0)) changes.push(`Spent: ₹${oldProj.spentLakh ?? 0} L → ₹${v.spentLakh ?? 0} L`);
                    if ((oldProj.hmPriority ?? false) !== hmPriority) changes.push(`HM Priority: ${oldProj.hmPriority ? 'Yes' : 'No'} → ${hmPriority ? 'Yes' : 'No'}`);
                    if ((oldProj.recurringIntervention ?? false) !== recurringIntervention) changes.push(`Recurring: ${oldProj.recurringIntervention ? 'Yes' : 'No'} → ${recurringIntervention ? 'Yes' : 'No'}`);
                }

                this.deptService.updateProjectInScheme(
                    this.drawerDept!.name, this.drawerScheme!.name,
                    this.editingSlNo!, { ...data, slNo: Number(v.slNo) }
                );
                this.drawerScheme = this.drawerDept!.schemes.find(s => s.name === this.drawerScheme!.name) ?? this.drawerScheme;
                this.showAddForm = false;
                this.formMode = 'add';
                this.editingSlNo = null;
                this.editingProjectId = null;
                this.editingProjectPhotos.set([]);
                this.syncDeptToFirestore();
                this.showUpdateToast(`"${v.roadName}" updated`, changes.length ? changes : ['No field changes detected']);

            } else {
                /* ── ADD MODE: always create in Firestore (single call, no duplicate) ── */
                const isDelayed: boolean = v.isDelayed ?? false;
                const newId = await this.firestoreProjectService.createProject({
                    name:                 v.roadName,
                    dept:                 this.drawerDept!.shortName,
                    loc:                  v.constituency ?? 'Dhamnagar',
                    cost:                 Number(v.costLakh),
                    spent:                v.spentLakh != null ? Number(v.spentLakh) : 0,
                    physical:             0,
                    status:               v.status,
                    start:                today,
                    end:                  fiscalYearEnd,
                    priority:             false,
                    hmPriority,
                    recurringIntervention,
                    isDelayed,
                    remarks:              '',
                    scheme:               this.drawerScheme!.name,
                    ...(v.gp             ? { gp: v.gp }                         : {}),
                    ...(v.yearOfCreation ? { yearOfCreation: v.yearOfCreation }  : {}),
                    ...(v.executantName  ? { executant: { name: v.executantName, contact: v.executantContact ?? '' } } : {}),
                    ...(v.district       ? { district: v.district }              : {}),
                    ...(this.drawerHasLength && v.lengthKm != null ? { roadLength: Number(v.lengthKm) } : {}),
                    ...(v.division       ? { division: v.division, block: v.division } : {}),
                });

                // Upload categorised photos (before/during/after) then persist
                const allStaged = [
                    ...this.stagedPhotosBefore(),
                    ...this.stagedPhotosDuring(),
                    ...this.stagedPhotosAfter(),
                ];
                if (allStaged.length > 0) {
                    try {
                        const urls = await Promise.all(allStaged.map(e => this.photoService.uploadPhoto(newId, e.file)));
                        // Persist as flat gallery + structured projectPhotos
                        const bLen = this.stagedPhotosBefore().length;
                        const dLen = this.stagedPhotosDuring().length;
                        await this.firestoreProjectService.updateProject(newId, {
                            photos: urls,
                            projectPhotos: {
                                before: urls.slice(0, bLen),
                                during: urls.slice(bLen, bLen + dLen),
                                after:  urls.slice(bLen + dLen),
                            },
                        });
                    } catch (photoErr) {
                        console.error('Photo upload failed (project was still created):', photoErr);
                    } finally {
                        this.clearAllStagedPhotos();
                    }
                }

                this.showAddForm = false;
                this.formMode = 'add';
                this.editingSlNo = null;
                this.showUpdateToast('Project Created Successfully', [`"${v.roadName}" added to ${this.drawerScheme!.name}`]);
                await new Promise<void>(resolve => setTimeout(resolve, 1200));
                await this.router.navigate(['/dhamnagar-dashboard/project', newId]);
            }

        } catch (err) {
            console.error('Failed to save project:', err);
            this.showUpdateToast('Save failed', ['An error occurred. Please try again.']);
        } finally {
            this.isSaving.set(false);
        }
    }

    // ── Firestore sync ────────────────────────────────────────────────────

    /**
     * Fire-and-forget: persist the current drawer dept's schemes to Firestore
     * after any mutation.
     *
     * Only the admin/minister/OSD roles have write access to the master
     * `departments` collection (per firestore.rules). Operators submit
     * project changes through the approval queue, so the dept master never
     * needs to update from their session. Skipping this side-effect for
     * non-admin roles avoids a spurious "Missing or insufficient permissions"
     * error in the console.
     */
    private syncDeptToFirestore(): void {
        if (!this.usingFirestore || !this.drawerDept) return;
        if (this.isOperator() || this.isApprover()) return;     // admin-only write target
        const id = this.firestoreIds.get(this.drawerDept.name);
        if (!id) return;
        this.firestoreDeptService.updateDeptSchemes(id, this.drawerDept.schemes)
            .catch(err => console.error('Dept Firestore sync failed', err));
    }

    // ── Bulk select / delete ──────────────────────────────────────────────

    toggleSelectProject(slNo: number): void {
        const s = new Set(this.selectedSlNos());
        if (s.has(slNo)) s.delete(slNo); else s.add(slNo);
        this.selectedSlNos.set(s);
    }

    toggleSelectAll(): void {
        const list = this.drawerScheme?.projectList ?? [];
        this.selectedSlNos.set(
            this.isAllSelected() ? new Set() : new Set(list.map(p => p.slNo))
        );
    }

    clearSelection(): void {
        this.selectedSlNos.set(new Set());
        this.showBulkDeleteConfirm.set(false);
    }

    async bulkDeleteProjects(): Promise<void> {
        this.isBulkDeleting.set(true);
        const slNos = [...this.selectedSlNos()];
        const fsIds = slNos
            .map(sn => this.drawerScheme?.projectList?.find(p => p.slNo === sn)?.id)
            .filter(Boolean) as string[];
        try {
            await Promise.all(fsIds.map(id => this.firestoreProjectService.deleteProject(id)));
            // Delete in reverse order so slNo indices remain stable
            slNos.sort((a, b) => b - a).forEach(sn =>
                this.deptService.deleteProjectFromScheme(this.drawerDept!.name, this.drawerScheme!.name, sn)
            );
            this.drawerScheme = this.drawerDept!.schemes.find(s => s.name === this.drawerScheme!.name) ?? this.drawerScheme;
            this.syncDeptToFirestore();
            this.selectedSlNos.set(new Set());
            this.showBulkDeleteConfirm.set(false);
            this.showUpdateToast(
                `${slNos.length} project${slNos.length > 1 ? 's' : ''} deleted`,
                ['Bulk deletion completed successfully']
            );
        } catch (err) {
            console.error('Bulk delete failed', err);
        } finally {
            this.isBulkDeleting.set(false);
        }
    }

    // ── Scheme CRUD ───────────────────────────────────────────────────────

    openAddSchemeModal(): void {
        this.schemeFormMode.set('add');
        this.editingSchemeIndex.set(-1);
        this.schemeForm = this.fb.group({
            name:      ['', [Validators.required, Validators.minLength(3)]],
            totalCost: [0,  [Validators.min(0)]],
            spent:     [0,  [Validators.min(0)]],
        });
        this.showSchemeForm.set(true);
    }

    openEditSchemeModal(scheme: SchemeCard, index: number, event: Event): void {
        event.stopPropagation();
        this.schemeFormMode.set('edit');
        this.editingSchemeIndex.set(index);
        this.schemeForm = this.fb.group({
            name:      [scheme.name,      [Validators.required, Validators.minLength(3)]],
            totalCost: [scheme.totalCost, [Validators.min(0)]],
            spent:     [scheme.spent,     [Validators.min(0)]],
        });
        this.showSchemeForm.set(true);
    }

    closeSchemeModal(): void {
        this.showSchemeForm.set(false);
    }

    submitSchemeForm(): void {
        if (this.schemeForm.invalid) {
            this.schemeForm.markAllAsTouched();
            return;
        }
        const v = this.schemeForm.value;
        if (this.schemeFormMode() === 'add') {
            const newScheme: SchemeCard = {
                name: v.name,
                projects: 0,
                totalCost: Number(v.totalCost ?? 0),
                spent: Number(v.spent ?? 0),
                completed: 0, inProgress: 0, stuck: 0, planned: 0,
            };
            this.selectedDept!.schemes.push(newScheme);
        } else {
            const idx = this.editingSchemeIndex();
            const existing = this.selectedDept!.schemes[idx];
            this.selectedDept!.schemes[idx] = {
                ...existing,
                name: v.name,
                totalCost: Number(v.totalCost ?? 0),
                spent: Number(v.spent ?? 0),
            };
            this.showUpdateToast(`Scheme "${v.name}" updated`, [`Changes saved to ${this.selectedDept!.name}`]);
        }
        // Refresh references so Angular detects the change
        this.selectedDept = { ...this.selectedDept!, schemes: [...this.selectedDept!.schemes] };
        this.departments = this.departments.map(d =>
            d.name === this.selectedDept!.name ? this.selectedDept! : d
        );
        this.deptService.setDepartments(this.departments);
        this.syncSelectedDeptToFirestore();
        this.showSchemeForm.set(false);
    }

    promptDeleteScheme(index: number, event: Event): void {
        event.stopPropagation();
        this.deletingSchemeIndex.set(index);
        this.showDeleteSchemeConfirm.set(true);
    }

    cancelDeleteScheme(): void {
        this.showDeleteSchemeConfirm.set(false);
        this.deletingSchemeIndex.set(-1);
    }

    confirmDeleteScheme(): void {
        const idx = this.deletingSchemeIndex();
        const schemeName = this.selectedDept!.schemes[idx]?.name ?? '';
        this.selectedDept!.schemes.splice(idx, 1);
        this.selectedDept = { ...this.selectedDept!, schemes: [...this.selectedDept!.schemes] };
        this.departments = this.departments.map(d =>
            d.name === this.selectedDept!.name ? this.selectedDept! : d
        );
        this.deptService.setDepartments(this.departments);
        this.syncSelectedDeptToFirestore();
        this.showDeleteSchemeConfirm.set(false);
        this.deletingSchemeIndex.set(-1);
        this.showUpdateToast(`Scheme deleted`, [`"${schemeName}" removed from ${this.selectedDept!.name}`]);
    }

    private syncSelectedDeptToFirestore(): void {
        if (!this.usingFirestore || !this.selectedDept) return;
        const id = this.firestoreIds.get(this.selectedDept.name);
        if (!id) return;
        this.firestoreDeptService.updateDeptSchemes(id, this.selectedDept.schemes)
            .catch(err => console.error('Scheme sync failed', err));
    }
}
